import {
  ArrowRightIcon,
  BankIcon,
  CreditCardIcon,
  HandCoinsIcon,
  PiggyBankIcon,
  ReceiptIcon,
  TargetIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router'

import { TarjetaTinte } from '@/components/Editorial'
import { haptic } from '@/lib/haptics'
import {
  useAccounts,
  useCards,
  useGoals,
  useIngresos,
  useRecurring,
  useReparto,
  useRevisiones,
} from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Qué le falta a tu PayBae para servirte bien.
 *
 * Existe porque la app sabía hacer mucho más de lo que el inicio dejaba ver.
 * El asistente de alta pregunta seis cosas; el resto —ingresos variables,
 * fondos de emergencia, pagos fijos, revisiones en pareja, el dinero sin
 * clasificar— vivía detrás de pantallas a las que solo se llega si ya sabes
 * que existen. Quien abría la app veía un tablero medio vacío y no tenía forma
 * de saber si el problema era la app o algo que él no había hecho.
 *
 * Esto lo dice en voz alta: esto ya está, esto falta, y este botón te lleva.
 * Desaparece solo cuando todo está listo — no es un panel permanente, es una
 * rampa de entrada.
 *
 * El progreso va en **anillo** y no en barra: es la forma que Daniel fijó para
 * toda la app.
 */

interface Tarea {
  clave: string
  titulo: string
  ayuda: string
  Icono: typeof BankIcon
  ruta: string
  listo: boolean
  /** Solo se pide cuando hay más de una persona en el hogar. */
  soloEnGrupo?: boolean
}

export function Preparacion() {
  const navigate = useNavigate()
  const { membership, profile } = useSession()

  const ingresos = useIngresos(membership.id)
  const cuentas = useAccounts()
  const tarjetas = useCards()
  const fijos = useRecurring()
  const metas = useGoals()
  const reparto = useReparto(membership.id)
  const revisiones = useRevisiones()

  const enGrupo = membership.household.kind !== 'PERSONAL'

  // Mientras cargan no se dibuja nada: enseñar una lista de pendientes que un
  // segundo después se marca sola es peor que esperar ese segundo.
  const cargando =
    ingresos.isPending ||
    cuentas.isPending ||
    tarjetas.isPending ||
    fijos.isPending ||
    metas.isPending
  if (cargando) return null

  const hayIngresoVariable = (ingresos.data ?? []).some((i) => i.kind === 'VARIABLE')
  const hayCuenta = (cuentas.data ?? []).length > 0
  const hayTarjeta = (tarjetas.data ?? []).length > 0
  const hayFijo = (fijos.data ?? []).length > 0
  const hayMeta = (metas.data ?? []).length > 0
  const hayReparto = (reparto.data?.items ?? []).length > 0
  // `frequency` es lo que se configura; que no haya revisiones aún es normal.
  const hayRevision = Boolean(revisiones.data?.frequency)

  const tareas: Tarea[] = [
    {
      clave: 'ingresos',
      titulo: 'De dónde viene tu dinero',
      ayuda: 'Tu sueldo y cada cuándo cae. Sin esto no podemos decirte cuánto te queda.',
      Icono: HandCoinsIcon,
      ruta: '/ajustes?ver=ingresos',
      listo: (ingresos.data ?? []).length > 0,
    },
    {
      clave: 'variable',
      titulo: 'Ingresos que no son fijos',
      ayuda: 'Comisiones, ventas, trabajos extra. Los apuntas una vez y te preguntamos cuando toque.',
      Icono: HandCoinsIcon,
      ruta: '/ajustes?ver=ingresos',
      listo: hayIngresoVariable,
    },
    {
      clave: 'cuentas',
      titulo: 'Dónde guardas tu dinero',
      ayuda: 'Tus cuentas, el efectivo y los vales. Solo el nombre: nunca pedimos números ni NIP.',
      Icono: BankIcon,
      ruta: '/ajustes?ver=cuentas',
      listo: hayCuenta,
    },
    {
      clave: 'tarjetas',
      titulo: 'Tus tarjetas de crédito',
      ayuda: 'Con su día de corte, para avisarte antes de que se te pase el pago.',
      Icono: CreditCardIcon,
      ruta: '/tarjetas',
      listo: hayTarjeta,
    },
    {
      clave: 'fijos',
      titulo: 'Lo que pagas cada mes',
      ayuda: 'Renta, luz, internet, colegiaturas. Lo que sale sí o sí.',
      Icono: ReceiptIcon,
      ruta: '/fijos',
      listo: hayFijo,
    },
    {
      clave: 'metas',
      titulo: 'Para qué estás ahorrando',
      ayuda: 'Un viaje, un coche, el fondo para imprevistos. Te decimos qué tan cerca vas.',
      Icono: PiggyBankIcon,
      ruta: '/ahorro',
      listo: hayMeta,
    },
    {
      clave: 'reparto',
      titulo: 'Cuánto apartas de cada ingreso',
      ayuda: 'El porcentaje que se va a ahorro, a metas y a emergencias en cuanto cobras.',
      Icono: TargetIcon,
      ruta: '/ahorro/reparto',
      listo: hayReparto,
    },
    {
      clave: 'revisiones',
      titulo: 'Su revisión en pareja',
      ayuda: 'Cada cuánto se sientan a revisar el ahorro juntos, con comprobantes si quieren.',
      Icono: UsersThreeIcon,
      ruta: '/ahorro/revisiones',
      listo: hayRevision,
      soloEnGrupo: true,
    },
  ]

  const visibles = tareas.filter((t) => !t.soloEnGrupo || enGrupo)
  const hechas = visibles.filter((t) => t.listo).length
  const total = visibles.length
  const pendientes = visibles.filter((t) => !t.listo)

  // Todo listo: la rampa se retira sola. Un panel de pendientes vacío que
  // sigue ocupando la mitad del inicio es ruido permanente.
  if (pendientes.length === 0) return null

  const porcentaje = Math.round((hechas / total) * 100)
  const nombre = (profile.displayName || '').split(' ')[0]

  return (
    // Una sola caja de papel, sin cabecera en tinta.
    //
    // La cabecera oscura tenía sentido cuando era el único bloque oscuro del
    // inicio. Con el bloque de tinta arriba quedaban dos bloques negros
    // apilados, y el segundo competía con la cifra principal justo debajo de
    // ella. Y solo se listan las que faltan: con seis de ocho hechas, las seis
    // tachadas ocupaban pantalla y media y empujaban «Dónde está tu dinero»
    // fuera de la vista. Lo hecho ya lo dice el anillo.
    <section className="mt-4">
      <TarjetaTinte className="flex flex-col gap-2">
        <header className="flex items-center gap-3.5 pb-1">
          <AnilloProgreso porcentaje={porcentaje} />
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-[17px] font-extrabold leading-tight tracking-[-0.03em] text-ink">
              {hechas === 0
                ? `Vamos a dejarla lista${nombre ? `, ${nombre}` : ''}`
                : 'Termina de preparar tu PayBae'}
            </h2>
            <p className="mt-0.5 text-[13px] leading-snug text-ink-soft">
              {hechas === 0
                ? 'Entre más le cuentes, mejor te dice cuánto puedes gastar hoy.'
                : `Llevas ${hechas} de ${total}. Te ${pendientes.length === 1 ? 'falta 1 cosa' : `faltan ${pendientes.length} cosas`} para que las cuentas te cuadren solas.`}
            </p>
          </div>
        </header>

        <ul className="flex flex-col">
          {pendientes.map((t) => (
            <li key={t.clave} className="border-t border-line first:border-t-0">
              <button
                type="button"
                onClick={() => {
                  haptic('light')
                  navigate(t.ruta)
                }}
                className="flex min-h-[56px] w-full items-center gap-3 py-2.5 text-left active:opacity-70"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-text">
                  <t.Icono size={17} weight="duotone" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold leading-snug text-ink">
                    {t.titulo}
                  </span>
                  <span className="mt-0.5 block text-[12px] leading-snug text-ink-soft">
                    {t.ayuda}
                  </span>
                </span>
                <ArrowRightIcon size={15} weight="bold" className="shrink-0 text-ink-faint" />
              </button>
            </li>
          ))}
        </ul>
      </TarjetaTinte>
    </section>
  )
}

/**
 * El progreso, en anillo.
 *
 * Una barra sería lo obvio y es justo lo que no va: la regla del proyecto es
 * que toda gráfica es circular. Además el anillo repite la forma del mapa del
 * dinero, así que las dos cosas que miden algo en el inicio se leen igual.
 */
function AnilloProgreso({ porcentaje }: { porcentaje: number }) {
  const R = 26
  const GROSOR = 6
  const circunferencia = 2 * Math.PI * R
  const avance = (porcentaje / 100) * circunferencia

  return (
    <div className="relative shrink-0" style={{ width: 64, height: 64 }}>
      <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden>
        <circle
          cx="32"
          cy="32"
          r={R}
          fill="none"
          stroke="var(--surface-sunken)"
          strokeWidth={GROSOR}
        />
        <circle
          cx="32"
          cy="32"
          r={R}
          fill="none"
          stroke="var(--income)"
          strokeWidth={GROSOR}
          strokeLinecap="round"
          strokeDasharray={`${avance} ${circunferencia - avance}`}
          // Arranca arriba y avanza como un reloj; sin esto empieza a las 3.
          transform="rotate(-90 32 32)"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-[15px] font-extrabold tracking-[-0.03em] text-ink">
        {porcentaje}%
      </span>
    </div>
  )
}
